import type { SelectInputOption } from '@/components/inputs/SelectInput';
import type { Option } from '@/components/inputs/AutocompleteInput';
import { AutocompleteInput } from '@/components';
import { useMemo, useState } from 'react';

const MIN_SEARCH_LENGTH = 3;

export type ExternalOperatorKind = 'headquarter' | 'subOperator';

interface ExternalOperatorAccessCardProps {
  isLoadingOptions: boolean;
  operatorsOptions: SelectInputOption[];
  value: string;
  onChange: (value: string) => void;
  error?: string;
  operatorKind: ExternalOperatorKind | null;
  parentOperatorName?: string;
  branchOptions: Option[];
  branchValue: string;
  onBranchChange: (value: string) => void;
  isLoadingBranches: boolean;
  branchError?: string;
}

const ExternalOperatorAccessCard = ({
  isLoadingOptions,
  operatorsOptions,
  value,
  onChange,
  error,
  operatorKind,
  parentOperatorName,
  branchOptions,
  branchValue,
  onBranchChange,
  isLoadingBranches,
  branchError,
}: ExternalOperatorAccessCardProps) => {
  const [inputValue, setInputValue] = useState('');
  const [branchInputValue, setBranchInputValue] = useState('');

  const selectedOption: Option | null = useMemo(() => {
    if (!value) return null;
    const match = operatorsOptions.find((operator) => operator.value === value);
    return match ? { label: match.label, data: match.value } : null;
  }, [value, operatorsOptions]);

  const filteredOptions: Option[] = useMemo(() => {
    const term = inputValue.trim().toLowerCase();
    if (term.length < MIN_SEARCH_LENGTH) return [];
    return operatorsOptions
      .filter((operator) => operator.label.toLowerCase().includes(term))
      .map((operator) => ({ label: operator.label, data: operator.value }));
  }, [inputValue, operatorsOptions]);

  const selectedBranch: Option | null = useMemo(
    () => branchOptions.find((branch) => branch.data === branchValue) ?? null,
    [branchOptions, branchValue]
  );

  const filteredBranchOptions: Option[] = useMemo(() => {
    const term = branchInputValue.trim().toLowerCase();
    if (!term || term === selectedBranch?.label.toLowerCase()) {
      return branchOptions;
    }
    return branchOptions.filter((branch) =>
      branch.label.toLowerCase().includes(term)
    );
  }, [branchInputValue, branchOptions, selectedBranch]);

  const isSubOperator = operatorKind === 'subOperator';
  const hasNoBranches =
    isSubOperator && !isLoadingBranches && branchOptions.length === 0;

  return (
    <div className='min-w-0 flex-1 rounded-lg border border-gray-200 bg-white p-6 shadow-sm'>
      <h3 className='mb-4 text-lg font-semibold text-gray-800'>
        Operador logístico
      </h3>
      <div className='min-w-0 overflow-hidden pt-3'>
        <AutocompleteInput
          name='operators.selected'
          placeholder='Seleccionar operador logístico'
          value={selectedOption}
          inputValue={inputValue}
          onChange={(option) => onChange(option ? String(option.data) : '')}
          onInputChange={(text) => setInputValue(text)}
          options={filteredOptions}
          loading={isLoadingOptions}
          required
          error={!!error}
          errorInfo={error}
        />
        <p className='mt-1 text-xs text-gray-500'>
          Seleccioná el operador logístico externo al que pertenece este
          usuario.
        </p>

        {operatorKind === 'headquarter' && (
          <div className='mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs leading-relaxed text-gray-700'>
            <span className='font-semibold text-emerald-800'>
              Invitando a casa central.{' '}
            </span>
            El usuario verá todas las sucursales de los comercios del operador
            y los pedidos de todos sus sub-operadores.
          </div>
        )}

        {isSubOperator && (
          <>
            <div className='mt-3 rounded-lg border border-[#ff750033] bg-[#ff75000a] p-3 text-xs leading-relaxed text-gray-700'>
              <span className='font-semibold text-gray-800'>
                Invitando a un sub-operador
                {parentOperatorName ? ` de ${parentOperatorName}` : ''}.{' '}
              </span>
              El usuario verá solo los pedidos de este operador en la sucursal
              que elijas, y solo esa sucursal para filtrar y crear pedidos.
            </div>

            <div className='mt-4'>
              <AutocompleteInput
                name='branches.selected'
                placeholder='Sucursal que atiende'
                value={selectedBranch}
                inputValue={branchInputValue}
                onChange={(option) =>
                  onBranchChange(option ? String(option.data) : '')
                }
                onInputChange={(text) => setBranchInputValue(text)}
                options={filteredBranchOptions}
                loading={isLoadingBranches}
                disabled={hasNoBranches}
                required
                error={!!branchError || hasNoBranches}
                errorInfo={
                  hasNoBranches
                    ? 'El sub-operador no tiene sucursales habilitadas: asocialo a un comercio con sucursales activas para poder invitar.'
                    : branchError
                }
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ExternalOperatorAccessCard;
